import {
  getEntityEventLogs,
  setEntityEventLogFilters,
} from '@state/reducers/entityEventLogReducer'
import { EventLogDto, EventEnum } from '@services/dtos'
import React, { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Box, CircularProgress, IconButton, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import moment from 'moment'
import { ConfigurationEventLabels } from '@utils/constants'
import { useIntl, IntlShape } from 'react-intl'

const PAGE_SIZE = 8

type Props = {
  entityId: string
  entityName: string
}

type DiffChange = {
  value: string
  added?: boolean
  removed?: boolean
  count: number
}

type DiffLine = { label: string; value: string; removed: boolean }

function parseDiffLines(diff: DiffChange[], intl: IntlShape): DiffLine[] {
  const result: DiffLine[] = []

  for (const chunk of diff) {
    if (!chunk.added && !chunk.removed) continue
    const lines = chunk.value.split('\n').filter(Boolean)

    for (const line of lines) {
      const rawLine = line
      const removed = !!chunk.removed

      // Split "fieldKey: value" → translate fieldKey
      const colonIdx = rawLine.indexOf(': ')
      if (colonIdx === -1) {
        result.push({ label: rawLine, value: '', removed })
        continue
      }
      const fieldKey = rawLine.slice(0, colonIdx).trim()
      const fieldValue = rawLine.slice(colonIdx + 2).trim()
      const translationId = `event-log.fields.${fieldKey}`
      const translated = intl.formatMessage({
        id: translationId,
        defaultMessage: fieldKey,
      })

      const valueTranslationId = `event-log.patientTransportStatus.${fieldValue}`
      const translatedValue = intl.formatMessage({
        id: valueTranslationId,
        defaultMessage: fieldValue,
      })
      result.push({ label: translated, value: translatedValue, removed })
    }
  }

  return result
}

const DiffBlock = ({
  diff,
  noChangesLabel,
  intl,
}: {
  diff: DiffChange[]
  noChangesLabel: string
  intl: IntlShape
}) => {
  const lines = parseDiffLines(diff, intl)

  if (lines.length === 0)
    return <span style={{ color: '#999' }}>{noChangesLabel}</span>

  return (
    <div style={{ fontSize: '0.8rem', lineHeight: 1.7 }}>
      {lines.map((line, i) => (
        <span
          key={i}
          className={`px-2 block border-l-2 font-semibold ${
            line.removed
              ? 'text-red-500 bg-red-50 border-l-red-500'
              : 'text-green-600 bg-green-50 border-l-green-600'
          }`}
        >
          {line.removed ? '− ' : '+ '}
          {line.label}
          {line.value ? `: ${line.value}` : ''}
        </span>
      ))}
    </div>
  )
}

function renderDetailsData(
  log: EventLogDto,
  noChangesLabel: string,
  documentLabel: string,
  intl: IntlShape,
): React.ReactNode {
  const { event, data } = log
  if (!data) return '—'

  switch (event) {
    case EventEnum.ORDER_UPDATED:
    case EventEnum.ORDER_COUVERTURE_UPDATED:
    case EventEnum.ORDER_PATIENT_UPDATED:
      if (data.diff) {
        return (
          <DiffBlock
            diff={data.diff as DiffChange[]}
            noChangesLabel={noChangesLabel}
            intl={intl}
          />
        )
      }
      return JSON.stringify(data)

    case EventEnum.ORDER_TRANSPORT_UPDATED:
    case EventEnum.ORDER_TRANSPORT_CREATED:
      if (data.diff) {
        return (
          <DiffBlock
            diff={data.diff as DiffChange[]}
            noChangesLabel={noChangesLabel}
            intl={intl}
          />
        )
      }
      return JSON.stringify(data)

    case EventEnum.ORDER_DOCUMENT_ADDED:
    case EventEnum.ORDER_DOCUMENT_REMOVED: {
      if (data.diff)
        return (
          <DiffBlock
            diff={data.diff as DiffChange[]}
            noChangesLabel={noChangesLabel}
            intl={intl}
          />
        )
      const docName = data.documentName ?? data.name ?? data.fileName ?? '—'
      return `${documentLabel} : ${docName}`
    }

    case EventEnum.ORDER_RECIPIENT_ADDED:
    case EventEnum.ORDER_RECIPIENT_REMOVED:
      return data.diff ? (
        <DiffBlock
          diff={data.diff as DiffChange[]}
          noChangesLabel={noChangesLabel}
          intl={intl}
        />
      ) : (
        '—'
      )

    case EventEnum.ORDER_PRECOTATION_ADDED:
    case EventEnum.ORDER_PRECOTATION_REMOVED:
      if (data.diff) {
        return (
          <DiffBlock
            diff={data.diff as DiffChange[]}
            noChangesLabel={noChangesLabel}
            intl={intl}
          />
        )
      }
      return '—'

    default:
      return JSON.stringify(data)
  }
}

export default function EventLogsOrderTable({ entityId, entityName }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const { items, loading, filters } = useAppSelector(
    (state) => state.entityEventLog,
  )
  const page = filters?.page ?? 0
  const noChangesLabel = intl.formatMessage({
    id: 'event-log.no-changes-detected',
  })
  const documentLabel = intl.formatMessage({ id: 'event-log.document-label' })

  useEffect(() => {
    dispatch(
      setEntityEventLogFilters({
        entityId,
        entityName,
        page: 0,
        limit: PAGE_SIZE,
      }),
    )
    dispatch(getEntityEventLogs())
    return () => {
      dispatch(
        setEntityEventLogFilters({
          entityId: undefined,
          entityName: undefined,
        }),
      )
    }
  }, [entityId, entityName])

  function goToPage(newPage: number) {
    dispatch(setEntityEventLogFilters({ page: newPage, limit: PAGE_SIZE }))
    dispatch(getEntityEventLogs())
  }

  const totalPages = Math.ceil((items.totalCount ?? 0) / PAGE_SIZE)

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: 2,
      }}
    >
      {loading && (
        <Box
          sx={{
            height: 400,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {!loading && (
        <>
          <Box
            sx={{
              minHeight: 400,
              maxHeight: 500,
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
            }}
          >
            <table
              style={{
                width: '100%',
                fontSize: '0.825rem',
                backgroundColor: 'white',
              }}
            >
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                }}
              >
                <tr className="bg-gray-200 text-left">
                  <th style={thStyle}>
                    {intl.formatMessage({ id: 'event-log.column-date' })}
                  </th>
                  <th style={thStyle}>
                    {intl.formatMessage({ id: 'event-log.column-action' })}
                  </th>
                  <th style={thStyle}>
                    {intl.formatMessage({ id: 'event-log.column-by' })}
                  </th>
                  <th style={thStyle}>
                    {intl.formatMessage({ id: 'event-log.column-details' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.datas.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      style={{ ...tdStyle, textAlign: 'center', color: '#999' }}
                    >
                      {intl.formatMessage({ id: 'event-log.no-events' })}
                    </td>
                  </tr>
                )}
                {items.datas.map((log: any) => (
                  <tr key={log.id}>
                    <td style={tdStyle}>
                      {moment(log.createdAt).format('DD/MM/YYYY HH:mm')}
                    </td>
                    <td style={tdStyle}>
                      {ConfigurationEventLabels[log.event] ?? log.event}
                    </td>
                    <td style={tdStyle}>
                      {log.createdBy?.member
                        ? `${log.createdBy.member.firstName} ${log.createdBy.member.lastName}`
                        : '—'}
                    </td>
                    <td style={tdStyle}>
                      {renderDetailsData(
                        log,
                        noChangesLabel,
                        documentLabel,
                        intl,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>

          {totalPages > 1 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                m: 1,
                gap: 1,
              }}
            >
              <IconButton
                onClick={() => goToPage(page - 1)}
                disabled={page === 0}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="body2">
                {page + 1} / {totalPages}
              </Typography>
              <IconButton
                onClick={() => goToPage(page + 1)}
                disabled={page + 1 >= totalPages}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontWeight: 600,
}

const tdStyle: React.CSSProperties = {
  padding: '10px',
  verticalAlign: 'top',
  borderBottom: '1px solid #e0e0e0',
}
